
import { Button } from "@/components/ui/button";
import { Plus, Moon, Sun } from "lucide-react";
import { NotificationCenter } from "@/components/notifications/NotificationCenter";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useTheme } from "@/contexts/ThemeContext";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useProjects } from "@/hooks/useProjects";

const DashboardHeader = () => {
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [projectName, setProjectName] = useState("");
  const [projectDescription, setProjectDescription] = useState("");
  
  const { createProject, isCreating } = useProjects();
  const { theme, setTheme } = useTheme();

  const toggleDarkMode = () => {
    const newTheme = theme === 'dark' ? 'light' : 'dark';
    setTheme(newTheme);
    toast.success(`${newTheme === 'dark' ? "Dark" : "Light"} mode enabled`);
  };

  const handleCreateProject = async () => {
    // Simple validation
    if (!projectName.trim()) {
      toast.error("Please enter a project name");
      return;
    }

    // Create the project using the hook
    const project = await createProject(projectName.trim(), projectDescription.trim() || undefined);
    
    if (project) {
      // Reset form and close sheet on success
      setProjectName("");
      setProjectDescription("");
      setIsSheetOpen(false);
    }
  };

  return (
    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
      <div>
        <h1 className="text-3xl font-bold text-foreground transition-colors">Dashboard</h1>
        <p className="text-muted-foreground mt-1 transition-colors">
          Welcome back! Here's your overview.
        </p>
      </div>
      <div className="flex gap-2">
        <NotificationCenter />
        <Button
          variant="outline"
          size="icon"
          onClick={toggleDarkMode}
          className="w-10 h-10 transition-colors"
          aria-label={theme === 'dark' ? "Switch to light mode" : "Switch to dark mode"}
        >
          {theme === 'dark' ? (
            <Sun className="h-4 w-4 text-foreground" />
          ) : (
            <Moon className="h-4 w-4 text-foreground" />
          )}
        </Button>
        
        <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
          <SheetTrigger asChild>
            <Button className="bg-primary text-primary-foreground hover:bg-primary/90 transition-colors">
              <Plus className="w-4 h-4 mr-2" />
              New Project
            </Button>
          </SheetTrigger>
          <SheetContent>
            <SheetHeader>
              <SheetTitle>Create New Project</SheetTitle>
              <SheetDescription>
                Add a new project to your dashboard. Fill out the details below.
              </SheetDescription>
            </SheetHeader>
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="projectName">Project Name *</Label>
                <Input 
                  id="projectName" 
                  placeholder="Enter project name" 
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                  disabled={isCreating}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="projectDescription">Description (Optional)</Label>
                <Input 
                  id="projectDescription" 
                  placeholder="Enter project description"
                  value={projectDescription}
                  onChange={(e) => setProjectDescription(e.target.value)}
                  disabled={isCreating}
                />
              </div>
              <Button 
                className="w-full mt-4" 
                onClick={handleCreateProject}
                disabled={isCreating || !projectName.trim()}
              >
                {isCreating ? "Creating..." : "Create Project"}
              </Button>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </div>
  );
};

export default DashboardHeader;
